import { useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@bookdental/ui";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { NewOrderStartDialog, type OrderStart } from "@/components/prosthesis/NewOrderStartDialog";
import { ProsthesisRequestForm } from "@/components/prosthesis/ProsthesisRequestForm";
import { ProsthesisOrdersCardView } from "@/components/prosthesis/ProsthesisOrdersCardView";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";
import { STATUS_META, TERMINAL_STATUSES } from "@/components/prosthesis/prosthesis-meta";
import type { ProsthesisOrderRow } from "@/components/prosthesis/types";

/** Filtros por situação, na leitura do dentista — não por status cru, que são seis. */
const FILTROS = [
  { chave: "todos", rotulo: "Todos" },
  { chave: "abertos", rotulo: "Em aberto" },
  { chave: "concluidos", rotulo: "Concluídos" },
] as const;

const emAberto = (s: ProsthesisStatus) => !TERMINAL_STATUSES.includes(s);

/**
 * Todos os pedidos de prótese do paciente num lugar só.
 *
 * O Histórico continua sendo o panorama — consultas, arquivos, alterações, tudo misturado em ordem
 * cronológica. Aqui o recorte é um só domínio, e por isso pode mostrar o que a linha do tempo não
 * mostra: situação de cada pedido, serviços, dentes, laboratório e prazo, com o detalhe a um clique.
 *
 * Não há query nova no servidor: `prosthesis.list` já aceita `patientId` e aplica o escopo de
 * sempre (sem `prosthesis.viewAll`, só os pedidos que o próprio usuário criou).
 */
export function PatientProtesesTab({
  patientId,
  patientName,
  patientPhone,
}: {
  patientId: string;
  patientName: string;
  patientPhone?: string | null;
}) {
  const [filtro, setFiltro] = useState<string>("todos");
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [inicioAberto, setInicioAberto] = useState(false);
  const [start, setStart] = useState<OrderStart | null>(null);

  const utils = trpc.useUtils();
  const query = trpc.prosthesis.list.useQuery({ patientId, take: 200, skip: 0 });
  // Memoizado por causa do `?? []`: o array vazio novo a cada render invalidaria os memos abaixo.
  const pedidos = useMemo(() => (query.data?.orders ?? []) as ProsthesisOrderRow[], [query.data]);

  const contagem = useMemo(
    () => ({
      todos: pedidos.length,
      abertos: pedidos.filter((o) => emAberto(o.status)).length,
      concluidos: pedidos.filter((o) => !emAberto(o.status)).length,
    }),
    [pedidos],
  );

  const visiveis = useMemo(() => {
    if (filtro === "abertos") return pedidos.filter((o) => emAberto(o.status));
    if (filtro === "concluidos") return pedidos.filter((o) => !emAberto(o.status));
    return pedidos;
  }, [pedidos, filtro]);

  const recarregar = () => {
    utils.prosthesis.list.invalidate();
    utils.patient.timeline.invalidate();
  };

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Próteses do paciente</h3>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            Todos os pedidos deste paciente, do mais recente ao mais antigo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInicioAberto(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
        >
          <Plus className="h-3.5 w-3.5" />
          Novo pedido
        </button>
      </div>

      {pedidos.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {FILTROS.map((f) => (
            <button
              key={f.chave}
              type="button"
              onClick={() => setFiltro(f.chave)}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
                filtro === f.chave
                  ? "bg-brand-600 font-semibold text-white"
                  : "text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted",
              )}
            >
              {f.rotulo}
              <span
                className={cn(
                  "tabular-nums",
                  filtro === f.chave ? "text-white/80" : "text-zinc-400 dark:text-muted-foreground/70",
                )}
              >
                {contagem[f.chave]}
              </span>
            </button>
          ))}
        </div>
      )}

      {visiveis.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50 p-8 text-center dark:border-border dark:bg-muted/20">
          <CoroaDentariaIcon className="mx-auto h-10 w-10 text-zinc-300 dark:text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-foreground">
            {pedidos.length === 0 ? "Nenhum pedido de prótese" : "Nenhum pedido nessa situação"}
          </p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            {pedidos.length === 0
              ? "Os pedidos feitos para este paciente aparecem aqui."
              : "Troque o filtro para ver os demais."}
          </p>
        </div>
      ) : (
        <>
          {/* Resumo por status: com muitos pedidos, saber quantos estão em cada etapa é a primeira
              pergunta — e ela não se responde contando cartão por cartão. */}
          <ResumoPorStatus pedidos={visiveis} />
          <ProsthesisOrdersCardView orders={visiveis} onOpen={setDetalheId} />
        </>
      )}

      <ProsthesisOrderDetailDialog orderId={detalheId} onClose={() => setDetalheId(null)} />

      <NewOrderStartDialog
        open={inicioAberto}
        onClose={() => setInicioAberto(false)}
        pacienteFixo={{ id: patientId, name: patientName, phone: patientPhone }}
        onConfirm={(s) => {
          setInicioAberto(false);
          setStart(s);
        }}
      />

      <Dialog open={!!start} onOpenChange={(v) => !v && setStart(null)}>
        <DialogContent className="max-h-[90vh] max-w-[1400px] overflow-y-auto dark:bg-card">
          <DialogTitle className="sr-only">Novo pedido</DialogTitle>
          {start && (
            <ProsthesisRequestForm
              start={start}
              onCreated={() => {
                setStart(null);
                recarregar();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResumoPorStatus({ pedidos }: { pedidos: ProsthesisOrderRow[] }) {
  const porStatus = useMemo(() => {
    const m = new Map<ProsthesisStatus, number>();
    for (const o of pedidos) m.set(o.status, (m.get(o.status) ?? 0) + 1);
    return [...m.entries()];
  }, [pedidos]);

  if (porStatus.length < 2) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {porStatus.map(([status, n]) => {
        const meta = STATUS_META[status];
        return (
          <span
            key={status}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            <span className="tabular-nums">{n}</span>
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}
